import ms from "milsymbol";

/** Visual paths for formations entering the theatre.
 *
 * These functions know nothing about MapLibre or the campaign clock. The
 * server has already decided that the formation exists and where it lands;
 * this module only gives that change a readable journey on the map.
 */

export type ArrivalMode = "air" | "sea" | "mainland";

export const AIR_ARRIVAL_MS = 6_500;
/** Landing craft are visible for the whole compressed approach. The server
 * books the opening exactly sixty game seconds ahead, so this journey ends at
 * the event instant at every clock speed instead of beginning after it. */
export const SEA_TRANSIT_GAME_S = 60;

const SICILY_CENTRE = { lat: 37.48, lon: 14.1 };
const SEA_APPROACH_DEGREES = 0.22;

/** Start a landing craft outside the coast, on the ray away from Sicily. */
export function seaApproach(lat: number, lon: number): { lat: number; lon: number } {
  // Longitude degrees are shorter at Sicily's latitude. Scaling x before
  // normalising makes the ray look radial on the rendered map, not merely in
  // the coordinate grid.
  const x = (lon - SICILY_CENTRE.lon) * Math.cos((lat * Math.PI) / 180);
  const y = lat - SICILY_CENTRE.lat;
  const length = Math.hypot(x, y) || 1;
  const dx = x / length;
  const dy = y / length;
  return {
    lat: lat + dy * SEA_APPROACH_DEGREES,
    lon: lon + (dx * SEA_APPROACH_DEGREES) / Math.cos((lat * Math.PI) / 180),
  };
}

const smooth = (p: number) => {
  const x = Math.max(0, Math.min(1, p));
  return x * x * (3 - 2 * x);
};

/** Geographic frame for a theatre entry. Airborne movement is vertical on the
 * screen, so its ground coordinate remains the destination throughout. */
export function arrivalFrame(
  mode: ArrivalMode,
  lat: number,
  lon: number,
  progress: number,
): { lat: number; lon: number } {
  if (mode === "air") return { lat, lon };
  const from = seaApproach(lat, lon);
  const p = smooth(progress);
  return {
    lat: from.lat + (lat - from.lat) * p,
    lon: from.lon + (lon - from.lon) * p,
  };
}

export function arrivalDuration(mode: ArrivalMode): number {
  // Sea and mainland transport are driven by game time and have no wall-clock
  // duration. This branch remains only for the shared animation type; neither
  // is started as a post-arrival unit animation.
  return mode === "air" ? AIR_ARRIVAL_MS : SEA_TRANSIT_GAME_S * 1000;
}

/** Fraction of a booked sea journey completed at this game-clock instant. */
export function seaTransitProgress(nowGameS: number, arrivalGameS: number): number {
  return Math.max(0, Math.min(1, (nowGameS - (arrivalGameS - SEA_TRANSIT_GAME_S)) / SEA_TRANSIT_GAME_S));
}

/** The standard friendly sea-surface landing-ship symbol (function `CALS`). */
export function transportShipMarkup(): string {
  const ship = new ms.Symbol("SFSPCALS-------", {
    size: 36,
    fill: true,
    fillOpacity: 1,
    colorMode: {
      Civilian: "#80e0ff",
      Friend: "#80e0ff",
      Hostile: "#80e0ff",
      Neutral: "#80e0ff",
      Unknown: "#80e0ff",
      Suspect: "#80e0ff",
    },
    iconColor: "#0b1016",
    outlineColor: "#0b1016",
    outlineWidth: 2,
  });
  return (
    '<div class="arrival-sea">' +
    '<span class="arrival-wake" aria-hidden="true"></span>' +
    `<div class="arrival-transport-symbol">${ship.asSVG()}</div>` +
    '</div>'
  );
}

/** Wrap the real NATO counter in the temporary vehicle that brings it in. */
export function arrivalMarkup(mode: ArrivalMode, symbol: string): string {
  if (mode === "air") {
    return (
      '<div class="arrival-flight">' +
      '<svg class="arrival-parachute" viewBox="0 0 96 62" aria-hidden="true">' +
      '<path class="canopy" d="M8 34C11 13 27 4 48 4s37 9 40 30c-8-6-16-6-24 0-6-6-12-6-16 0-5-6-11-6-16 0-8-6-16-6-24 0Z" />' +
      '<path class="cords" d="M9 34 38 54M32 34l11 20m21-20L53 54m34-20L58 54" />' +
      '</svg>' +
      `<div class="arrival-unit-symbol">${symbol}</div>` +
      '</div>'
    );
  }
  return transportShipMarkup();
}
